import { useEffect, useRef, useState } from "react";

/** Upward scroll that must accumulate before a hidden header returns. A thumb
 *  resting on the screen produces small upward jitters while reading; without
 *  a floor the bar would flicker in and out on them. */
const REVEAL_THRESHOLD_PX = 30;

/** Downward scroll tolerated before anything hides, so the bar does not react
 *  to the first few pixels of a scroll that started at the top of the page. */
const HIDE_AFTER_PX = 64;

/** Movement below this is noise — a trackpad's tail end, a rounding step. */
const NOISE_FLOOR_PX = 2;

/**
 * How long to disregard scrolling after a toggle. Collapsing the header
 * changes its height, which moves everything below it, and the browser's
 * scroll anchoring compensates by scrolling — a scroll this hook would
 * otherwise read as the guest's own and immediately act on, toggling back.
 * Must outlast the CSS transition that causes the reflow.
 */
const SETTLE_MS = 350;

/**
 * Whether a scroll-away header should currently be out of the way.
 *
 * Deliberately asymmetric: hiding follows the scroll immediately, revealing
 * asks for {@link REVEAL_THRESHOLD_PX} of deliberate upward movement first.
 */
export const useHideOnScroll = (enabled = true): boolean => {
  const [hidden, setHidden] = useState(false);
  const hiddenRef = useRef(false);
  const lastY = useRef(0);
  const upDistance = useRef(0);
  const settledAt = useRef(0);

  useEffect(() => {
    if (!enabled) {
      hiddenRef.current = false;
      setHidden(false);
      return;
    }

    lastY.current = window.scrollY;
    upDistance.current = 0;

    const apply = (next: boolean) => {
      if (next === hiddenRef.current) return;
      hiddenRef.current = next;
      settledAt.current = performance.now() + SETTLE_MS;
      upDistance.current = 0;
      setHidden(next);
    };

    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;
      lastY.current = y;

      // Our own reflow, not the guest: follow the position, decide nothing.
      if (performance.now() < settledAt.current) return;

      if (y <= HIDE_AFTER_PX) {
        upDistance.current = 0;
        apply(false);
        return;
      }

      if (delta > NOISE_FLOOR_PX) {
        upDistance.current = 0;
        apply(true);
        return;
      }

      if (delta < -NOISE_FLOOR_PX) {
        upDistance.current += -delta;
        if (upDistance.current >= REVEAL_THRESHOLD_PX) apply(false);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled]);

  return hidden;
};
